<template>
    <div>
        <div v-if="child">
            <control label="Child Dependency Until" for="method">
                <select id="method" v-model="innerLifeExpectancy.method" v-field-control required @change="calcChildDependentAge">
                    <option value="" disabled hidden>Please select...</option>
                    <option value="dependent_until_16">Aged 16</option>
                    <option value="dependent_until_18">Aged 18</option>
                    <option value="dependent_until_21">Aged 21</option>
                    <option value="dependent_until_custom">Specify custom dependency</option>
                </select>
            </control>
            <control label="Age Dependency Ends"
                     before-after="years"
                     v-if="innerLifeExpectancy.method === 'dependent_until_custom'">
                <input autocomplete="off" type="number"
                       v-model="innerLifeExpectancy.child.youthCutOffAge"
                       @input="calcChildDependentDate">
            </control>
        </div>
        <div v-else>
            <control :label="label" for="method1" :icon-space="!dayOne">
                <select id="method1" v-model="innerLifeExpectancy.method" @change="calcAgeAtDeath">
                    <option value="ogden_table" v-if="this.person.gender === null" disabled hidden>Please select...</option>
                    <option value="ogden_table" v-if="this.person.gender === 'male'">Odgen Table 1</option>
                    <option value="ogden_table" v-if="this.person.gender === 'female'">Odgen Table 2</option>
                    <option value="years_and_months">Enter Years/Months</option>
                    <option value="enter_date">Enter Date</option>
                    <option value="age_at_death">Enter Age At Death (years)</option>
                    <option value="percentage_of_normal_life_expectancy">% of Normal Life Expectancy</option>
                    <option value="percentage_reduction_in_normal_life_expectancy">% Reduction in Normal Life Expectancy</option>
                </select>
            </control>
            <control label="Enter Years/Months From Today" v-if="innerLifeExpectancy.method=='years_and_months'" :icon-space="!dayOne">
                <div class="a-form-inputs--half">
                    <div class="a-form-inputs__container">
                        <select id="years" v-model.number="innerLifeExpectancy.yearsAndMonths.years"
                                @change="calcAgeAtDeath">
                            <option v-for="year in 76" :value="year - 1">{{ year - 1}} year(s)</option>
                        </select>
                    </div>
                    <div class="a-form-inputs__container">
                        <select id="months" v-model.number="innerLifeExpectancy.yearsAndMonths.months"
                                @change="calcAgeAtDeath">
                            <option v-for="month in 12" :value="month - 1">
                                {{ month - 1 }} month(s)
                            </option>
                        </select>
                    </div>
                </div>
            </control>
            <control label="Percentage of Normal Life Expectancy" for="percentage_of_normal_life_expectancy"
                     v-if="innerLifeExpectancy.method=='percentage_of_normal_life_expectancy'"
                     before-after="percent" :icon-space="!dayOne">
                <input autocomplete="off" v-model.number="percentageOfNormalLifeExpectancy"
                       :disabled="innerLifeExpectancy.method!=='percentage_of_normal_life_expectancy'"
                       v-field-control
                       v-two-dp
                       required
                       max="100"
                       min="1"
                       @input="calcAgeAtDeath"
                       name="percentage_of_normal_life_expectancy"
                       type="number">
                <control-error :show="$error('percentage_of_normal_life_expectancy')">
                    The percentage of normal life expectancy must be number between 1 and 100
                </control-error>
            </control>
            <control label="Percentage Reduction in Normal Life Expectancy"
                     for="percentage_reduction_in_normal_life_expectancy"
                     v-if="innerLifeExpectancy.method=='percentage_reduction_in_normal_life_expectancy'"
                     :icon-space="!dayOne"
                     before-after="percent">
                <input autocomplete="off" v-model.number="percentageReductionInNormalLifeExpectancy"
                       :disabled="innerLifeExpectancy.method!=='percentage_reduction_in_normal_life_expectancy'"
                       v-field-control
                       required
                       max="100"
                       min="1"
                       @input="calcAgeAtDeath"
                       name="percentage_reduction_in_normal_life_expectancy"
                       type="number">
                <control-error :show="$error('percentage_reduction_in_normal_life_expectancy')">
                    The percentage reduction in normal life expectancy must be number between 1 and 100
                </control-error>
            </control>
            <control label="Expected End of Life Date" for="expected_date_of_death"
                     :icon-space="!dayOne"
                     v-if="innerLifeExpectancy.method=='enter_date'">
                <date-input v-model="innerLifeExpectancy.dateOfDeathIsAssumed"
                            v-field-control:expected_date_of_death.required="{type:'date'}"
                            :disabled="innerLifeExpectancy.method!='enter_date'"
                            @input="calcAgeAtDeath"></date-input>
                <control-error :show="$error('expected_date_of_death')">
                    The deceased's expected end of life is required and must be in the format DD/MM/YYYY
                </control-error>
            </control>
            <control label="Expected Age at End of Life (Years)" for="life_age_at_death"
                     :icon-space="!dayOne"
                     :modal-name="'life_age_at_death_modal_' + uniqueId"
                     :endpoint="innerLifeExpectancy.method"
                     :payload="lifeExpectancyModal"
                     :tip-icon="lifeAgeAtDeathIcon"
                     :tip-show="stepValid"
                     tip-text="Click to view calculation" tip-side="left"
                     tip-width="140"
                     tip-name="life_age_at_death"
                     :before-after="dayOne ? 'years-old-on-dark' : 'years-old'">
                <input autocomplete="off" v-model.number="ageAtDeath"
                       :disabled="innerLifeExpectancy.method!=='age_at_death'"
                       v-field-control
                       v-two-dp
                       required
                       min="20"
                       @input="calcAgeAtDeath"
                       name="life_age_at_death"
                       type="number">
            </control>
            <control label="Expected End of Life Date" for="life_date_of_death"
                     :icon-space="!dayOne"
                     v-if="innerLifeExpectancy.method!=='enter_date'">
                <input autocomplete="off" type="text" :value="dateOfDeath" name="life_date_of_death"
                       disabled v-field-control="{type:'date'}" required>
                <control-error :show="$error('life_date_of_death') &&
                        (innerLifeExpectancy.method=='percentage_reduction_in_normal_life_expectancy' ||
                        innerLifeExpectancy.method=='percentage_of_normal_life_expectancy')">
                </control-error>
            </control>
        </div>
        <modal :name="'life_age_at_death_modal_' + uniqueId" no-default-button="true">
            <modal-content></modal-content>
        </modal>
    </div>
</template>
<script>
    import _ from 'lodash';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import DateInput from 'components/form/control/DateInput.vue';
    import FormValidator from 'components/form/validator/mixin';
    import Modal from 'components/modal/Modal.vue';
    import moment from 'moment';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import {mapGetters, mapActions} from 'vuex';
    import ModalContent from 'components/modal/ModalContent.vue';
    import {validDate} from 'components/form/validator/validations';
    import { isStepValid }  from 'services/routingMethods';

    /**
     * Used to determine the life expectancy from a given "person"
     */
    export default {
        name: 'life-expectancy',
        props: ['label', 'person', 'type', 'index', 'date', 'assumed-settlement', 'dayOne', 'stepValid'],
        computed: {
            // Do not modify start directly
            innerLifeExpectancy() {
                return this.person.lifeExpectancy;
            },
            minDateOfDeath() {
                if (this.person.dateOfDeath) {
                    return this.person.dateOfDeath;
                }

                return this.date;
            },
            ageAtDeath: {
                get() {
                    return this.person.lifeExpectancy.ageAtDeath > 0 ? this.person.lifeExpectancy.ageAtDeath : null;
                },
                set(value) {
                    this.innerLifeExpectancy.ageAtDeath = value;
                }
            },
            dateOfDeath: {
                get() {
                    return this.person.lifeExpectancy.ageAtDeath > 0 ? this.person.lifeExpectancy.dateOfDeath : null;
                }
            },
            child: {
                get() {
                    if (this.person.dateOfDeath || !isStepValid(2)) {
                        return false;
                    }
                    if (moment(this.person.dateOfBirth, "DD/MM/YYYY") > moment().subtract(21, 'years')
                        && this.innerLifeExpectancy.method === 'years_and_months') {
                        this.innerLifeExpectancy.method = '';
                        return true;
                    }
                    return moment(this.person.dateOfBirth, "DD/MM/YYYY") > moment().subtract(21, 'years')
                }
            },
            lifeDateOfDeathOptions() {
                return {type: 'date', rules: 'min:' + this.minDateOfDeath};
            },
            percentageOfNormalLifeExpectancy: {
                get() {
                    return this.person.lifeExpectancy.percentageOfNormalLifeExpectancy >= 0 ?
                        this.person.lifeExpectancy.percentageOfNormalLifeExpectancy : null;
                },
                set(value) {
                    this.innerLifeExpectancy.percentageOfNormalLifeExpectancy = value;
                }
            },
            percentageReductionInNormalLifeExpectancy: {
                get() {
                    return this.person.lifeExpectancy.percentageReductionInNormalLifeExpectancy >= 0 ?
                        this.person.lifeExpectancy.percentageReductionInNormalLifeExpectancy : null;
                },
                set(value) {
                    this.innerLifeExpectancy.percentageReductionInNormalLifeExpectancy = value;
                }
            },
            lifeAgeAtDeathIcon: {
                get() {
                    return (this.innerLifeExpectancy.method === 'ogden_table'
                        || this.innerLifeExpectancy.method === 'percentage_of_normal_life_expectancy'
                        || this.innerLifeExpectancy.method === 'percentage_reduction_in_normal_life_expectancy')
                        ? 'formula'
                        : 'default';
                }
            },
            lifeExpectancyValue: {
                get() {
                    if (this.innerLifeExpectancy.method === 'percentage_of_normal_life_expectancy') {
                        return this.person.lifeExpectancy.percentageOfNormalLifeExpectancy;
                    }

                    if (this.innerLifeExpectancy.method === 'percentage_reduction_in_normal_life_expectancy') {
                        return this.person.lifeExpectancy.percentageReductionInNormalLifeExpectancy;
                    }
                }
            },
            lifeExpectancyModal: {
                get() {
                    return {
                        type: this.type,
                        assumedSettlement: this.assumedSettlement,
                        person: this.person,
                        value: this.lifeExpectancyValue,
                    }
                },
            },
            uniqueId() {
                return this._uid;
            }
        },
        components: {
            Control,
            ControlError,
            DateInput,
            Modal,
            ModalContent
        },
        mixins: [
            FormValidator
        ],
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        watch: {
            // If any of the these values change, update
            ['person.dateOfBirth']()  {
                this.calcAgeAtDeath();
            },
            ['person.dateOfDeath']()  {
                this.calcAgeAtDeath();
            },
            ['person.gender']()  {
                this.calcAgeAtDeath();
            }
        },
        methods: {
            ...mapActions('modal', ['toggle']),
            calcAgeAtDeath () {
                if (this.child && this.innerLifeExpectancy.method === 'ogden_table') {
                    this.innerLifeExpectancy.method = '';
                }

                if (!this.child && (this.innerLifeExpectancy.method === 'dependent_until' || this.innerLifeExpectancy.method === '')) {
                    this.innerLifeExpectancy.method = 'ogden_table';
                }

                if (validDate(this.person.dateOfBirth)) {
                    this.$emit('update', this.innerLifeExpectancy);
                }
            },
            calcChildDependentAge(age) {
                switch (this.innerLifeExpectancy.method) {
                    case 'dependent_until_16' :
                        this.innerLifeExpectancy.child.youthCutOffAge = 16;
                        break;
                    case 'dependent_until_18' :
                        this.innerLifeExpectancy.child.youthCutOffAge = 18;
                        break;
                    case 'dependent_until_21' :
                        this.innerLifeExpectancy.child.youthCutOffAge = 21;
                        break;
                    case 'dependent_until_custom' :
                        this.innerLifeExpectancy.child.youthCutOffAge = this.youthCutOffAge;
                        break;
                }

                this.innerLifeExpectancy.child.youthCutOffDate = moment(this.person.dateOfBirth, 'DD/MM/YYYY')
                    .add(this.innerLifeExpectancy.child.youthCutOffAge, 'years')
                    .format('DD/MM/YYYY');

                this.$emit('update', this.innerLifeExpectancy);
            },
            calcChildDependentDate() {
                this.$emit('update', this.innerLifeExpectancy);
            },
        }
    }
</script>


// WEBPACK FOOTER //
// LifeExpectancy.vue?604620e8